import { type Integration } from '@sentry/core'
import { type Breadcrumb, type BreadcrumbHint, type ErrorEvent, type EventHint, init } from '@sentry/react'

import { authTransitions } from '@/sentry/authTransitionRefusal.ts'
import { ErrorWithSentryCaptureContext } from '@/sentry/ErrorWithSentryCaptureContext.ts'

const typeConsoleColor = (breadcrumb: Breadcrumb) => {
  switch (true) {
    case breadcrumb.type === 'error':
    case breadcrumb.level === 'error':
      return 'background-color: crimson; color: white'
    case breadcrumb.type === 'debug':
    case breadcrumb.level === 'debug':
      return 'background-color: silver; color: white'
    default:
      return 'background-color: grey; color: white'
  }
}

const categoryConsoleColor = (category?: string) => {
  switch (category) {
    case 'firestore':
      return 'background-color: purple; color: white'
    default:
      return 'color: blue'
  }
}

const style = 'border-radius: 0.5em; font-weight: bold; padding: 2px 0.5em;'

const breadcrumbToConsole = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint) => {
  const consoleLevel = breadcrumb.level === 'debug' || breadcrumb.type === 'debug' ? 'debug' : ('info' as const)

  /* eslint-disable no-console */
  console[consoleLevel](
    `%cBreadcrumb%c %c${breadcrumb.category}`,
    `${style} ${typeConsoleColor(breadcrumb)}`,
    '',
    `${style} ${categoryConsoleColor(breadcrumb.category)}`,
    breadcrumb.message,
    breadcrumb.data ?? hint?.data
  )
  if (import.meta.env.DEV && hint) {
    console.groupCollapsed('Breadcrumb Data')
    console.info(breadcrumb)
    console.info('hint', hint)
    console.groupEnd()
  }
  /* eslint-enable no-console */
}

const getConsoleLevel = (event: ErrorEvent) => {
  switch (event.level) {
    case 'fatal':
    case 'error':
      return 'error'
    case 'warning':
      return 'warn'
    case 'log':
      return 'log'
    case 'info':
      return 'info'
    case 'debug':
      return 'debug'
    default:
      return 'log'
  }
}

const prefix = (event: ErrorEvent) => {
  switch (event.level) {
    case 'fatal':
    case 'error':
      return [`%cError%c ${event.level}`, `${style} background-color: crimson; color: white`, 'color: red']
    case 'warning':
      return [`%cWaring%c ${event.level}`, `${style} background-color: orange; color: white`, 'color: orange']
    default:
      return [`%cInfo%c ${event.level}`, `${style} background-color: #6082B6; color: white`, 'color: #6082B6']
  }
}

function errorToConsole(event: ErrorEvent, hint?: EventHint) {
  /* eslint-disable no-console */
  console[getConsoleLevel(event)](...prefix(event), event.message ?? hint?.originalException)
  console.groupCollapsed('Sentry Event Data')
  if (event.level === 'error' && hint?.originalException) {
    console.error('originalException', hint.originalException)
  }
  if (hint?.originalException instanceof Error && hint.originalException.cause) {
    console.error('cause', hint.originalException.cause)
  }
  if (event.tags) {
    console.info('tags', event.tags)
  }
  if (event.contexts) {
    console.info('contexts', event.contexts)
  }
  if (event.extra) {
    console.info('extra', event.extra)
  }
  console.groupEnd()
  /* eslint-enable no-console */
}

const processErrorWithSentryCaptureContext = (event: ErrorEvent, exception: unknown) => {
  if (exception instanceof ErrorWithSentryCaptureContext) {
    exception.beforeSend(event)
    if (exception.cause) {
      processErrorWithSentryCaptureContext(event, exception.cause)
    }
  }
}

export const initSentry = (integrations: Integration[] = []) => {
  init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations,
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    // Session Replay
    replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
    replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
    beforeSend: (event, hint) => {
      if (authTransitions.isAuthTransitionRefusal(hint.originalException)) {
        return null
      }
      processErrorWithSentryCaptureContext(event, hint.originalException)
      if (import.meta.env.MODE === 'development' || import.meta.env.DEV) {
        errorToConsole(event, hint)
      }
      return event
    },
    beforeBreadcrumb: (breadcrumb, hint) => {
      if (import.meta.env.MODE === 'development' || import.meta.env.DEV) {
        breadcrumbToConsole(breadcrumb, hint)
      }
      if ((breadcrumb.level === 'debug' || breadcrumb.type === 'debug') && import.meta.env.MODE !== 'development') {
        return null
      }
      return breadcrumb
    },
  })
}
